import clsx from 'clsx';
import {
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import Colors from '@dynatrace/strato-design-tokens/colors';

import { MicroguideContext } from './contexts/MicroguideContext.js';
import * as styles from './Microguide.sty.js';
import { Step } from './Step.js';
import { Button } from '../../buttons/button/Button.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { Surface } from '../../layouts/surface/Surface.js';
import { useKeyboardFocusHandling } from '../../overlays/hooks/useKeyboardFocusHandling.js';
import { Content } from '../shared-components/Content.js';
import { Title } from '../shared-components/Title.js';
import { Visual } from '../shared-components/Visual.js';

const COMPONENT_NAME = 'Microguide';

//#region Typings
/**
 * Accepted properties for Microguide
 * @public
 */
export interface MicroguideProps
  extends WithChildren,
    AriaLabelingProps,
    DataTestId,
    MaskingProps,
    StylingProps,
    BehaviorTrackingProps {
  /**
   * Whether the Microguide is currently shown or not. Has to be controlled by the consumer.
   */
  open: boolean;
  /**
   * Placement of the Microguide.
   * @defaultValue 'bottom-right'
   */
  placement?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /**
   * Callback fired when closing the Microguide.
   * Indicates the current step (zero-based) when closing and whether the Microguide was finished (i.e. closed in the last step).
   * @param currentStep - The currently active step when closing.
   * @param finished - Whether the guide was closed in the last step.
   */
  onClose: (currentStep: number, finished: boolean) => void;
  /**
   * Callback fired when the user navigates between steps. Indicates the target step (zero-based).
   * @param step - The step that the user navigated to.
   */
  onStepChange?: (step: number) => void;
}
//#endregion Typings

const MicroguideRoot = forwardRef<HTMLDivElement, MicroguideProps>(
  (props, forwardedRef) => {
    const {
      open,
      placement = 'bottom-right',
      onClose,
      children,
      onStepChange,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;

    const microguideContainer = useRef<HTMLElement | null>(null);
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);

    const [currentStep, setCurrentStep] = useState(0);
    const [isNext, setIsNext] = useState(true);
    const [hasTitle, setHasTitle] = useState(false);
    const [hasContent, setHasContent] = useState(false);
    const [handleFocusOnKeydown, closedOnKeydown] =
      useKeyboardFocusHandling(closeButtonRef);
    const overlayStacking = useOverlayStacking(undefined, 'microguide');
    const intl = useIntl();

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const [slots] = useSlots(children, {
      steps: { match: Step, multiple: true },
    });

    const stepsLength = slots.steps?.length ?? 0;
    const isLastStep = currentStep + 1 >= stepsLength;
    const nextGotItDataDtProperties = `state:${
      isLastStep ? 'gotIt' : 'next'
    },step:${currentStep + 1},totalSteps:${stepsLength}`;

    if (hasTitle && ariaLabelingProps['aria-labelledby'] === undefined) {
      ariaLabelingProps['aria-labelledby'] = 'strato-microguide-title';
    }
    if (hasContent && ariaLabelingProps['aria-describedby'] === undefined) {
      ariaLabelingProps['aria-describedby'] = 'strato-microguide-content';
    }

    // Handle the escape key
    const closeOnEscape = (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      closedOnKeydown.current = true;
      onClose(currentStep, isLastStep);
    };

    useLayoutEffect(() => {
      const container = document.createElement('div');
      container.setAttribute('aria-live', 'polite');
      container.setAttribute('data-microguide-container', 'true');
      microguideContainer.current = container;

      // Insert before the toast container so toasts always render on top.
      const toastContainer = document.querySelector('[data-toast-container]');
      if (toastContainer?.parentNode) {
        toastContainer.parentNode.insertBefore(container, toastContainer);
      } else {
        document.body.appendChild(container);
      }

      return () => {
        microguideContainer.current?.remove();
      };
    }, []);

    useEffect(() => {
      // Reset microguide to the first step once it closes
      if (open === false) {
        setCurrentStep(0);
        // Restore focus if closed via keyboard
        handleFocusOnKeydown();
      }
    }, [handleFocusOnKeydown, open]);

    const handleBack = () => {
      const previousStep = currentStep - 1;
      setIsNext(false);
      onStepChange?.(previousStep);
      setCurrentStep(previousStep);
    };

    const handleNext = () => {
      const nextStep = currentStep + 1;
      setIsNext(true);
      onStepChange?.(nextStep);
      setCurrentStep(nextStep);
    };

    return open
      ? createPortal(
          <Surface
            ref={forwardedRef}
            elevation="floating"
            onKeyDown={closeOnEscape}
            role="dialog"
            aria-modal="false"
            className={clsx(
              styles.wrapper,
              styles.wrapperPosition[placement],
              consumerClassName,
            )}
            style={{
              // since the surface applies `position: relative` by default,
              // we ensure higher specificity by applying position as an inline style
              position: 'fixed',
              zIndex: overlayStacking.style.zIndex,
              ...consumerStyle,
            }}
            data-testid={dataTestId}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
            {...ariaLabelingProps}
            {...behavioralTrackingProps}
            {...forwardProps}
            data-dt-component={COMPONENT_NAME}
          >
            <MicroguideContext.Provider
              value={{
                onClose,
                currentStep,
                totalSteps: stepsLength,
                isNext,
                setHasContent,
                setHasTitle,
                closeButtonRef,
                closedOnKeydown,
              }}
            >
              {slots.steps?.[currentStep]}
            </MicroguideContext.Provider>
            <Flex
              className={styles.footer}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap={16}
              paddingX={16}
              paddingBottom={16}
              paddingTop={8}
            >
              <div style={{ color: Colors.Text.Neutral.Subdued }}>
                {stepsLength > 1 && `${currentStep + 1}/${stepsLength}`}
              </div>
              <Flex gap={8}>
                {currentStep > 0 && (
                  <Button onClick={handleBack} data-dt-children-name="Back">
                    <FormattedMessage
                      description="Microguide Back button."
                      defaultMessage="Back"
                      id="N27hQ6LyGqdym2Yi"
                    />
                  </Button>
                )}
                <Button
                  variant={isLastStep ? 'accent' : 'emphasized'}
                  color={isLastStep ? 'primary' : 'neutral'}
                  onClick={
                    isLastStep ? () => onClose(currentStep, true) : handleNext
                  }
                  onKeyDown={() => (closedOnKeydown.current = isLastStep)}
                  data-dt-children-name="NextGotIt"
                  data-dt-properties={nextGotItDataDtProperties}
                >
                  {isLastStep
                    ? intl.formatMessage({
                        defaultMessage: 'Got it',
                        id: 'fzkORq3AsvX/NtMB',
                        description: 'Microguide Got it button at last step.',
                      })
                    : intl.formatMessage({
                        defaultMessage: 'Next',
                        id: '+jn6HNnzB4IDIbZu',
                        description: 'Microguide Next button.',
                      })}
                </Button>
              </Flex>
            </Flex>
          </Surface>,
          microguideContainer.current ?? document.body,
        )
      : null;
  },
);

(
  MicroguideRoot as typeof MicroguideRoot & { displayName: string }
).displayName = COMPONENT_NAME;

/**
 * Use the `Microguide` to guide users through features or functionality in small steps, supporting onboarding of new users and the introduction of new features.
 * @public
 */
export const Microguide = Object.assign(MicroguideRoot, {
  Step,
  Title,
  Visual,
  Content,
});
